import { Component, Inject, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { SafeUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { LANGUAGES } from '@core/constants';
import { UpdateUserModel, UserProfile } from '@core/models';
import { UploadPhotoDialogComponent } from '@custom-components/overlay/upload-photo-dialog/upload-photo-dialog.component';

export interface EditProfileDialogData {
  userProfile: UserProfile | undefined;
  /** Current avatar (resolved by the caller's UserStore), shown as the initial preview. */
  avatar?: SafeUrl | string;
}

export interface EditProfileDialogResult {
  /** Changed fields only. */
  user: UpdateUserModel;
  /** New cropped avatar, when the user picked one. */
  avatar?: Blob;
}
import {
  AvatarComponent,
  DialogShellComponent,
  FilledButtonComponent,
  SelectFieldComponent,
  TextButtonComponent,
  TextFieldComponent,
} from '@custom-components';

@Component({
  selector: 'xemex-edit-profile-dialog',
  imports: [
    ReactiveFormsModule,
    MatSelectModule,
    TranslateModule,
    AvatarComponent,
    DialogShellComponent,
    TextFieldComponent,
    SelectFieldComponent,
    FilledButtonComponent,
    TextButtonComponent,
  ],
  templateUrl: './edit-profile-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-profile-dialog.component.scss',
})
export class EditProfileDialogComponent {
  private readonly dialog = inject(MatDialog);

  readonly LANGUAGES = LANGUAGES;

  /** Current avatar shown as the initial preview; replaced by the cropped result after upload. */
  croppedImage: SafeUrl | string | undefined;
  private imageBlob: Blob | undefined;

  readonly profileForm = new FormGroup({
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    email: new FormControl({ value: '', disabled: true }, [Validators.email]),
    phone: new FormControl(''),
    language: new FormControl(''),
  });

  private readonly userProfile: UserProfile | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: EditProfileDialogData,
    private readonly dialogRef: MatDialogRef<EditProfileDialogComponent, EditProfileDialogResult>,
  ) {
    this.userProfile = data.userProfile;
    this.croppedImage = data.avatar;

    this.profileForm.setValue({
      firstName: this.userProfile?.firstName ?? '',
      lastName: this.userProfile?.lastName ?? '',
      email: this.userProfile?.email ?? '',
      phone: this.userProfile?.phone ?? '',
      language: this.userProfile?.language ?? '',
    });
  }

  hasError(controlName: keyof typeof this.profileForm.controls, errorCode: string): boolean {
    const control = this.profileForm.controls[controlName];
    return control.hasError(errorCode) && (control.touched || control.dirty);
  }

  get hasAvatar(): boolean {
    return !!this.croppedImage;
  }

  onRemoveAvatar() {
    // No delete-avatar endpoint exists yet, so this only clears a newly picked avatar locally.
    this.croppedImage = undefined;
    this.imageBlob = undefined;
  }

  onUploadAvatar() {
    this.dialog
      .open(UploadPhotoDialogComponent, {
        width: '640px',
        height: '456px',
        maxWidth: '640px',
        data: {},
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.croppedImage = result.croppedImage;
          this.imageBlob = result.imageBlob;
        }
      });
  }

  onSave() {
    if (this.profileForm.invalid) {
      return;
    }

    const formValue = this.profileForm.getRawValue();
    const user: UpdateUserModel = { ...formValue } as UpdateUserModel;

    if (user.email === this.userProfile?.email) delete user.email;
    if (user.firstName === this.userProfile?.firstName) delete user.firstName;
    if (user.lastName === this.userProfile?.lastName) delete user.lastName;
    if (user.language === this.userProfile?.language) delete user.language;
    if (!user.phone?.length || user.phone === this.userProfile?.phone) delete user.phone;

    // The caller owns the UserStore instance, so persistence happens there (see ProfilePage).
    this.dialogRef.close({ user, avatar: this.imageBlob });
  }

  onCancel() {
    this.dialogRef.close();
  }
}
